import type { FC, ReactNode } from "react";
import type { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import {
  caseToOperateThunks,
  CTOTitle as Title,
  FieldsArray,
  FileTypes,
  FormDraftButton,
  getMitigationDefault,
  PrimaryButton,
  type FileProps,
  type Response,
  type ResponsePostCto,
  type StepProps,
  translate,
} from "@shape/reef-shared";
import { FilesField, TextAreaField } from "components/Form";
import { AssigningRecommendation } from "pages/cto/pages/CtoDetails/components/Step2Evaluation/AssigningRecommendation/AssigningRecommendation";
import { getNewStep2AffectedAreas } from "services/cto";
import type { CreateStep2MdbFormValues, Step2Response } from "types";
import { SectionTitle } from "../../../SectionTitle";
import { AffectedAreas, validateAffectedAreasForSubmit } from "../AffectedAreas";
import { Step2AssessmentTeam } from "../Step2AssessmentTeam";
import styles from "../../NewStep2Evaluation.module.scss";

interface SmeAssessmentReviewProps {
  createdStep2: Response<ResponsePostCto>;
  cto: StepProps<Step2Response>["cto"];
  ctoId: string;
  fieldsArrayControl: UseFieldArrayReturn<CreateStep2MdbFormValues, "assigning_recommendations">;
  flaggedQuestions: boolean;
  form: UseFormReturn<CreateStep2MdbFormValues>;
  handleDraftSubmit: () => void;
  hasSisModification?: boolean | null;
  initialValues: Step2Response;
  isCurrentVersion: boolean;
  isSisModificationEnabledForContext: boolean;
  onUploadSuccess: () => void;
  readOnly: boolean;
  rejectedReviewDetails?: ReactNode;
  showActions: boolean;
  showRecommendations: boolean;
  submitDisabled: boolean;
  validateSisModificationAssessment: () => boolean;
  vesselId: string;
}

export const SmeAssessmentReview: FC<SmeAssessmentReviewProps> = ({
  createdStep2,
  cto,
  ctoId,
  fieldsArrayControl,
  flaggedQuestions,
  form,
  handleDraftSubmit,
  hasSisModification,
  initialValues,
  isCurrentVersion,
  isSisModificationEnabledForContext,
  onUploadSuccess,
  readOnly,
  rejectedReviewDetails,
  showActions,
  showRecommendations,
  submitDisabled,
  validateSisModificationAssessment,
  vesselId,
}) => (
  <>
    <div className={styles.section}>
      <TextAreaField
        form={form}
        label={translate("ctoStep2.diagnosis")}
        name="potential_consequences_affected"
        rules={{ required: !readOnly }}
        placeholder={translate("formGeneric.typeHereMandatory")}
        readOnly={readOnly}
        defaultValue={initialValues.potential_consequences_affected ?? ""}
      />
    </div>

    {isSisModificationEnabledForContext && (
      <div className={styles.section}>
        <SectionTitle>{translate("ctoStep2.sisModificationAssessment")}</SectionTitle>
        {hasSisModification && (
          <p className="sis-modification-assessment-mandatory">
            {translate("ctoStep2.sisModificationAssessmentMandatory")}
          </p>
        )}
        <TextAreaField
          form={form}
          label={translate("generic.justification")}
          name="sis_modification_justification"
          placeholder={translate("generic.typeHere")}
          readOnly={readOnly}
          defaultValue={initialValues?.sis_modification_justification ?? ""}
          rules={{
            maxLength: {
              value: 1000,
              message: translate("genericComponents.maxLength", { maxChars: 1000 }),
            },
            validate: validateSisModificationAssessment,
          }}
        />

        {!readOnly && <Title>{translate("ctoStep1.uploadAndAttachFiles")}</Title>}
        <FilesField
          form={form}
          borderWidth={2}
          name="sis_modification_justification_files"
          readOnly={readOnly}
          defaultValue={initialValues?.sis_modification_justification_files}
          rules={{
            validate: validateSisModificationAssessment,
          }}
          downloadFile={(file: FileProps) =>
            caseToOperateThunks.downloadFile({
              attachment_id: file.attachment_id,
              file_name: file.file_name,
              vesselId,
              ctoId,
              shouldUseHistory: !isCurrentVersion,
            })
          }
          uploadFiles={(files: File[]) =>
            caseToOperateThunks.uploadFiles(
              vesselId,
              ctoId,
              onUploadSuccess,
              files,
              FileTypes.MODIFICATION_ASSESSMENT
            )
          }
          vesselId={vesselId}
          ctoId={ctoId}
        />
      </div>
    )}

    <div className={styles.section}>
      <Step2AssessmentTeam
        form={form}
        readOnly={readOnly}
        initialValues={initialValues.assessment_team}
      />
    </div>

    <div className={styles.section}>
      <AffectedAreas
        affectedAreaTags={initialValues.affected_area_tags}
        form={form}
        loadAffectedAreaAccordion={(tagCode, mahCode) =>
          getNewStep2AffectedAreas(vesselId, ctoId, tagCode, mahCode)
        }
        readOnly={readOnly}
      />
    </div>

    {showRecommendations && (
      <>
        <div className={styles.recommendations}>
          <FieldsArray
            control={fieldsArrayControl}
            form={form}
            name="assigning_recommendations"
            label={translate("ctoStep2.assigningRecommendation")}
            defaultValue={readOnly ? initialValues.assigning_recommendations : [{}]}
            readOnly={readOnly}
            showAddButtonName={translate("ctoStep2.addNewRecommendation")}
            renderRow={(prefix, _field, index) => (
              <AssigningRecommendation
                form={form}
                initialValues={
                  initialValues.assigning_recommendations[index] || getMitigationDefault()
                }
                readOnly={readOnly}
                prefix={prefix}
                index={index}
                vesselId={vesselId}
                tags={cto.tags ?? []}
                isNewCto={true}
                hasTagsSelectField={true}
                flaggedQuestions={flaggedQuestions}
              />
            )}
          />
        </div>

        <div className={styles.section}>
          {!readOnly && <Title>{translate("ctoStep1.uploadAndAttachFiles")}</Title>}
          <FilesField
            form={form}
            borderWidth={2}
            name="assigning_recommendation_files"
            readOnly={readOnly}
            defaultValue={initialValues.assigning_recommendation_files}
            downloadFile={(file: FileProps) =>
              caseToOperateThunks.downloadFile({
                attachment_id: file.attachment_id,
                file_name: file.file_name,
                vesselId,
                ctoId,
                shouldUseHistory: !isCurrentVersion,
              })
            }
            uploadFiles={(files: File[]) =>
              caseToOperateThunks.uploadFiles(
                vesselId,
                ctoId,
                onUploadSuccess,
                files,
                FileTypes.RECOMMENDATION_FILES
              )
            }
            vesselId={vesselId}
            ctoId={ctoId}
          />
        </div>
      </>
    )}

    {rejectedReviewDetails}

    {showActions && (
      <div className={styles.actions}>
        <PrimaryButton
          type="submit"
          disabled={createdStep2.loading || submitDisabled}
          onClick={() => {
            form.setValue("draft", false);
            const affectedAreasAreValid = validateAffectedAreasForSubmit({
              affectedAreaTags: initialValues.affected_area_tags,
              isDraft: false,
              selectedSelections: form.getValues("affected_areas"),
            });

            if (affectedAreasAreValid) {
              form.clearErrors("affected_areas");
              return;
            }

            form.setError("affected_areas", {
              type: "required",
              message: translate("ctoStep2.affectedAreas.required"),
            });
          }}
        >
          {createdStep2.loading && !form.getValues("draft")
            ? translate("formGeneric.submitting")
            : translate("formGeneric.submitForm")}
        </PrimaryButton>

        <FormDraftButton
          form={form}
          disabled={submitDisabled}
          readOnly={readOnly}
          isSubmitting={createdStep2.loading}
          handleSubmit={handleDraftSubmit}
        />
      </div>
    )}
  </>
);
